import React, { useEffect } from 'react';
import { observer } from 'mobx-react';
import { useStores } from '@app/hooks/stores';
import { Row, Col, Tabs, Card, Checkbox } from 'antd';
import dayjs from 'dayjs';
import relativeTime from 'dayjs/plugin/relativeTime';
import utc from 'dayjs/plugin/utc';
import RoomsStatus from '@app/modules/Dashboard/components/RoomsStatus';
import { SummaryCard } from '@app/common/SummaryCard';
import './Dashboard.less';
import { CheckboxChangeEvent } from 'antd/lib/checkbox';
import Spinner from '@app/common/Spinner/Spinner';

dayjs.extend(utc);
dayjs.extend(relativeTime);

const reportIntervalMs = 10 * 60 * 1000; // 10mins

const { TabPane } = Tabs;

const Dashboard: React.FC = () => {
  const { dashboardStore } = useStores();
  const {
    handleDashboardTabChange,
    loading,
    buildings,
    getReportRoomsByBuildingData,
    setDashboard24HRangeDate,
    showOnlyActiveRooms,
    switchActiveRoomsRender,
    dubaiTZ,
    selectedBuildingReport,
    initialDashboardDataLoadFetch,
    getAllBuildingsPolling,
    getBuildingReportPolling
  } = dashboardStore;

  const handleShowActiveRooms = (e: CheckboxChangeEvent) => {
    switchActiveRoomsRender(e.target.checked);
    getReportRoomsByBuildingData();
  };

  const handle24HRange = () => {
    setDashboard24HRangeDate({
      beginDate: dayjs().utcOffset(dubaiTZ).subtract(1, 'day').utc().format(),
      endDate: dayjs().utcOffset(dubaiTZ).utc().format()
    });
  };

  const dashboardDataPolling = () => {
    getAllBuildingsPolling();
    getBuildingReportPolling();
  };

  useEffect(() => {
    handle24HRange();
    initialDashboardDataLoadFetch();
    const fetchBuildingsPullInterval = setInterval(dashboardDataPolling, reportIntervalMs);
    const roomsDataPullInterval = setInterval(getReportRoomsByBuildingData, reportIntervalMs);

    return () => {
      clearInterval(fetchBuildingsPullInterval);
      clearInterval(roomsDataPullInterval);
    };
  }, []);

  const onTabChange = key => {
    handle24HRange();
    switchActiveRoomsRender(false);
    handleDashboardTabChange(+key);
  };

  if (!buildings) return <>No buildings available</>;
  if (!selectedBuildingReport) return <Spinner />;

  return (
    <Tabs className="dashboard" onChange={onTabChange}>
      {buildings &&
        buildings.map(building => (
          <TabPane tab={building.name} key={building.id}>
            <Row gutter={24}>
              <Col lg={8} md={16} sm={24}>
                <SummaryCard
                  title="Building Power consumption"
                  number={selectedBuildingReport.lastWeekDailyAverage}
                  numberDiff={selectedBuildingReport.weeklyDailyAvgPercentageChange}
                  loading={loading}
                  postfix="kWh"
                  description={
                    <>
                      Daily average for past 7 days <br />
                      Compared to previous 7 day period
                    </>
                  }
                />
              </Col>
              <Col lg={8} md={16} sm={24}>
                <SummaryCard
                  title="Weather impact"
                  number={selectedBuildingReport.lastWeekCDDAverage}
                  numberDiff={selectedBuildingReport.weeklyCDDAvgPercentageChange}
                  loading={loading}
                  postfix="CDD"
                  description={
                    <>
                      Average Cooling Degree Day value for last 7 days <br />
                      Compared to previous 7 day period
                    </>
                  }
                />
              </Col>
            </Row>
            <Row gutter={24}>
              <Col lg={24} md={24} sm={24}>
                <Card
                  title="Rooms"
                  extra={
                    <Checkbox checked={showOnlyActiveRooms} onChange={handleShowActiveRooms}>
                      Show only active rooms
                    </Checkbox>
                  }
                  className="table-card"
                >
                  <RoomsStatus handleDateUpdate={handle24HRange} />
                </Card>
              </Col>
            </Row>
          </TabPane>
        ))}
    </Tabs>
  );
};

export default observer(Dashboard);
